<template>
  <div
    class="results-toolbar lg:tw-border-b lg:tw-border-slate-200"
    :class="{ 'sort-toolbar-open': showSortDropdown }"
  >
    <div class="toolbar-inner tw-premium-container tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-py-3 lg:tw-flex-wrap">
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-sm tw-text-slate-600">
        <button
          type="button"
          class="toolbar-ship-btn tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-white tw-px-3 tw-py-2 tw-shadow-sm tw-transition-all tw-pressable hover:tw-shadow-card hover:tw-ring-1 hover:tw-ring-brand/20"
          @click="$bvModal.show('ship-to-currency-modal')"
        >
          {{ $l('shipTo') }}
          <img class="tw-h-4 tw-w-6 tw-rounded-[2px] tw-object-cover" :src="countryFlagUrl" alt="">
          <strong class="tw-text-slate-900">{{ currencySign }}</strong>
          <outline-icon name="chevron-down" size="sm" />
        </button>
        <span
          v-if="resultCount"
          class="toolbar-result-count tw-hidden tw-text-slate-500 lg:tw-inline"
        >
          {{ resultCount }} results
        </span>
      </div>

      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <watchlist-panel class="toolbar-watchlist tw-hidden lg:tw-block" />

        <button type="button" class="tw-outline-button tw-pressable" @click="$emit('toggle-locale')">
          {{ locale === 'he' ? 'EN' : 'HE' }}
        </button>

        <div class="sort-dropdown-wrap tw-hidden lg:tw-block" v-click-outside="closeSortDropdown">
          <button
            type="button"
            class="tw-outline-button tw-pressable"
            :aria-expanded="showSortDropdown"
            aria-haspopup="listbox"
            @click.stop="toggleSortDropdown"
          >
            <outline-icon name="sort" size="sm" />
            {{ $l('sortBy') }}
            <outline-icon name="chevron-down" size="sm" />
          </button>

          <div
            v-show="showSortDropdown"
            class="sort-dropdown-menu"
            role="listbox"
            :aria-label="$l('sortBy')"
          >
            <button
              v-for="option in sortOptions"
              :key="option.value"
              type="button"
              role="option"
              class="sort-dropdown-option tw-pressable"
              :class="{ 'sort-dropdown-option--active': selectedSorting === option.value }"
              :aria-selected="selectedSorting === option.value"
              @click="selectSort(option.value)"
            >
              <span
                class="sort-dropdown-radio"
                :class="{ 'sort-dropdown-radio--active': selectedSorting === option.value }"
              >
                <span
                  v-if="selectedSorting === option.value"
                  class="sort-dropdown-radio-dot"
                />
              </span>
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>

        <ship-to-currency-modal
          :selected-country="selectedCountry"
          :selected-currency="selectedCurrency"
          @apply="$emit('shipping-change', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OutlineIcon from './OutlineIcon.vue';
import WatchlistPanel from './WatchlistPanel.vue';
import ShipToCurrencyModal from './ShipToCurrencyModal.vue';

export default {
  name: 'ResultsToolbar',
  components: {
    OutlineIcon,
    WatchlistPanel,
    ShipToCurrencyModal,
  },
  props: {
    countryFlagUrl: {
      type: String,
      required: true,
    },
    selectedCountry: {
      type: String,
      default: 'US',
    },
    selectedCurrency: {
      type: String,
      default: 'USD',
    },
    currencySign: {
      type: String,
      default: '$',
    },
    resultCount: {
      type: Number,
      default: 0,
    },
    selectedSorting: {
      type: String,
      default: 'compopo-sort',
    },
  },
  data() {
    return {
      showSortDropdown: false,
      skipOutsideClose: false,
      sortOptions: [
        { value: 'compopo-sort', label: 'Compopo Sort' },
        { value: 'lowest-first', label: 'Price - Lowest First' },
        { value: 'highest-first', label: 'Price - Highest First' },
      ],
    };
  },
  methods: {
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
      if (this.showSortDropdown) {
        this.skipOutsideClose = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.skipOutsideClose = false;
          }, 0);
        });
      }
    },
    closeSortDropdown() {
      if (this.skipOutsideClose) {
        return;
      }
      this.showSortDropdown = false;
    },
    selectSort(value) {
      this.$emit('sort', value);
      this.closeSortDropdown();
    },
  },
};
</script>

<style scoped>
.sort-toolbar-open {
  position: relative;
  z-index: 60;
}

@media (min-width: 1024px) {
  .results-toolbar {
    background: #f8fafc;
  }
}

@media (max-width: 1023px) {
  .results-toolbar .toolbar-watchlist {
    display: none !important;
  }

  .results-toolbar {
    border-bottom: none !important;
  }

  .toolbar-inner {
    flex-wrap: nowrap;
    padding-top: 0;
  }

  .toolbar-inner > div {
    flex-shrink: 0;
  }
}

.sort-dropdown-wrap {
  position: relative;
}

.sort-dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 70;
  min-width: 220px;
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 0.5rem 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 8px 24px rgba(15, 23, 42, 0.12);
}

[dir='rtl'] .sort-dropdown-menu {
  right: auto;
  left: 0;
}

.sort-dropdown-option {
  display: flex;
  width: 100%;
  cursor: pointer;
  align-items: center;
  gap: 0.75rem;
  border: 0;
  background: transparent;
  padding: 0.625rem 1rem;
  text-align: start;
  font-size: 0.875rem;
  color: #334155;
  transition: background-color 0.15s ease;
}

.sort-dropdown-option:hover {
  background: #f8fafc;
}

.sort-dropdown-option--active {
  background: #eff6ff;
  font-weight: 600;
  color: #2563eb;
}

.sort-dropdown-radio {
  display: flex;
  height: 1rem;
  width: 1rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid #cbd5e1;
}

.sort-dropdown-radio--active {
  border-color: #2563eb;
  background: #2563eb;
}

.sort-dropdown-radio-dot {
  height: 0.375rem;
  width: 0.375rem;
  border-radius: 9999px;
  background: #fff;
}
</style>
